import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { ArrowIcon, ClockIcon } from "@/components/icons/UiIcons";
import { CategoryBadge } from "./CategoryBadge";
import type { PlaceCard } from "./shared";

/**
 * One place in the Discover grid. Pure presentational (works in both server
 * and client trees): everything is already localized in `place`. The whole
 * card is one link target via the title; the image is not a link, so it can
 * carry the title as alt text without announcing two identical links.
 */
export function AttractionCard({
  place,
  categoryName,
  categoryColor,
  exploreLabel,
  visitLabel,
  priority = false,
  compact = false,
  selected = false,
  onHover,
}: {
  place: PlaceCard;
  categoryName: string;
  categoryColor: string;
  exploreLabel: string;
  visitLabel: string;
  priority?: boolean;
  /** Smaller variant for the list next to the map. */
  compact?: boolean;
  selected?: boolean;
  onHover?: (slug: string | null) => void;
}) {
  const href = `/discover/${place.slug}`;
  // Only attach handlers when a client parent asked for them: this component
  // is also rendered from Server Components, where function props are invalid.
  const hoverProps = onHover
    ? {
        onMouseEnter: () => onHover(place.slug),
        onMouseLeave: () => onHover(null),
      }
    : {};

  if (compact) {
    return (
      <article
        // `relative` contains the stretched link (after:inset-0) to this card;
        // without it the link fills the nearest positioned ancestor — on the
        // stay page that was the whole page, so every click opened the last card.
        className={`group relative flex gap-3 rounded-xl border p-2 transition-colors ${
          selected
            ? "border-forest-400 bg-forest-50"
            : "border-transparent hover:border-sand hover:bg-cream"
        }`}
        {...hoverProps}
      >
        <div className="relative h-16 w-20 shrink-0 overflow-hidden rounded-lg">
          <Image
            src={place.image}
            alt={place.title}
            fill
            sizes="80px"
            className="object-cover"
          />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold text-bark">
            <Link
              href={href}
              className="after:absolute after:inset-0 focus-visible:outline-none"
            >
              {place.title}
            </Link>
          </h3>
          <p className="mt-0.5 truncate text-xs text-stone">{place.place}</p>
          <span className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-medium text-stone">
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: categoryColor }}
              aria-hidden
            />
            {categoryName}
          </span>
        </div>
      </article>
    );
  }

  return (
    <article
      className="card-lift group relative flex h-full flex-col overflow-hidden rounded-2xl border border-sand bg-cream"
      {...hoverProps}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <Image
          src={place.image}
          alt={place.title}
          fill
          priority={priority}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          className="img-zoom object-cover"
        />
        <div className="absolute left-3 top-3">
          <CategoryBadge name={categoryName} color={categoryColor} />
        </div>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-display text-xl font-bold leading-snug text-bark">
          <Link
            href={href}
            className="after:absolute after:inset-0 after:rounded-2xl focus-visible:outline-none group-focus-within:underline"
          >
            {place.title}
          </Link>
        </h3>
        <p className="mt-1 text-xs text-stone">{place.place}</p>
        <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-stone">
          {place.summary}
        </p>
        <div className="mt-auto flex items-center justify-between gap-3 pt-5 text-sm">
          {place.visit ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-stone">
              <ClockIcon className="h-4 w-4 text-forest-400" />
              <span className="sr-only">{visitLabel}: </span>
              {place.visit}
            </span>
          ) : (
            <span />
          )}
          <span className="inline-flex items-center gap-1.5 font-semibold text-forest-600 transition-transform group-hover:translate-x-0.5">
            {exploreLabel}
            <ArrowIcon className="h-4 w-4" />
          </span>
        </div>
      </div>
    </article>
  );
}
